import { ConfigService } from '@config/config.service';
import { CallState, SipState } from '@main/models/sip-state';
import { MainActions, MainActionTypes } from '@main/ngrx/actions/main.actions';
import { CallResponse } from '@services/associations/model/callResponse';
import { ManagerEventResponse } from '@services/associations/model/managerEventResponse';
import { MinimizedDialogInfoModel } from '@shared/models/minimized-dialog-info.model';
import { PaginationModel } from '@shared/models/pagination.model';

export const mainFeatureKey = 'main';

export interface State {
  minimizedDialogs: MinimizedDialogInfoModel[],
  sipState: SipState,
  callState: CallState,
  callsList: CallResponse[] | null,
  callsListPagination: PaginationModel,
  callsListLoading: boolean,
  notificationsList: ManagerEventResponse[] | null,
  notificationsListPagination: PaginationModel,
  notificationsListLoading: boolean,
}

export const initialState: State = {
  minimizedDialogs: [],
  sipState: {},
  callState: {},
  callsList: null,
  callsListPagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
  callsListLoading: false,
  notificationsList: null,
  notificationsListPagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
  notificationsListLoading: false,
};

export function reducer(state = initialState, action: MainActions): State {
  let clonedMinimizedDialogs = null;

  switch (action.type) {
    /** Minimize Dialog */
    case MainActionTypes.MinimizeDialog:
      clonedMinimizedDialogs = JSON.parse(JSON.stringify(state.minimizedDialogs));

      if (clonedMinimizedDialogs && clonedMinimizedDialogs.length) {
        const existingDialogToMinimizeIndex = clonedMinimizedDialogs.findIndex((item: MinimizedDialogInfoModel) => {
          return item.dialogId === action.payload.dialogId;
        });
        const existingDialogToMinimize: MinimizedDialogInfoModel = clonedMinimizedDialogs.find((item: MinimizedDialogInfoModel) => {
          return item.dialogId === action.payload.dialogId;
        });

        if (existingDialogToMinimizeIndex !== -1 && existingDialogToMinimize) {
          existingDialogToMinimize.active = false;
          existingDialogToMinimize.restored = false;
          clonedMinimizedDialogs.splice(existingDialogToMinimizeIndex, 1, existingDialogToMinimize);
        } else {
          if (action.payload && action.payload.new && clonedMinimizedDialogs && clonedMinimizedDialogs.length) {
            for (const index in clonedMinimizedDialogs) {
              // eslint-disable-next-line no-prototype-builtins
              if (clonedMinimizedDialogs.hasOwnProperty(index)) {
                clonedMinimizedDialogs[index].active = false;
              }
            }
          }

          clonedMinimizedDialogs = clonedMinimizedDialogs && clonedMinimizedDialogs.length ?
            [...clonedMinimizedDialogs, action.payload && action.payload.new ? {...action.payload, active: true} : action.payload] :
            [action.payload && action.payload.new ? {...action.payload, active: true} : action.payload];
        }

        /** Make Last Resumed Dialog active */
        const restoredDialogs = clonedMinimizedDialogs.filter((item: MinimizedDialogInfoModel) => item.restored);
        if (restoredDialogs && restoredDialogs.length) {
          const lastRestoredDialog = restoredDialogs[restoredDialogs.length - 1];
          if (lastRestoredDialog) {
            lastRestoredDialog.active = true;
            const dialogIndexToReplaceIndex = clonedMinimizedDialogs.findIndex((item: MinimizedDialogInfoModel) => {
              return item.dialogId === lastRestoredDialog.id;
            });
            const dialogIndexToReplace = clonedMinimizedDialogs.find((item: MinimizedDialogInfoModel) => {
              return item.dialogId === lastRestoredDialog.id;
            });
            if (dialogIndexToReplaceIndex !== -1 && dialogIndexToReplace) {
              clonedMinimizedDialogs.splice(dialogIndexToReplaceIndex, 1, lastRestoredDialog);
            }
          }
        }
      } else {
        clonedMinimizedDialogs = [action.payload];
      }

      return {
        ...state,
        minimizedDialogs: clonedMinimizedDialogs,
      };

    /** Restore Minimized Dialog */
    case MainActionTypes.RestoreMinimizedDialog:
      clonedMinimizedDialogs = JSON.parse(JSON.stringify(state.minimizedDialogs));
      // eslint-disable-next-line no-case-declarations
      const dialogToRestoreIndex = clonedMinimizedDialogs && clonedMinimizedDialogs.length ?
        clonedMinimizedDialogs.findIndex((item: MinimizedDialogInfoModel) => item.dialogId === action.payload) :
        -1;
      // eslint-disable-next-line no-case-declarations
      const dialogToRestore: MinimizedDialogInfoModel = clonedMinimizedDialogs && clonedMinimizedDialogs.length ?
        clonedMinimizedDialogs.find((item: MinimizedDialogInfoModel) => item.dialogId === action.payload) :
        null;

      /** Make all dialogs inactive */
      for (const index in clonedMinimizedDialogs) {
        // eslint-disable-next-line no-prototype-builtins
        if (clonedMinimizedDialogs.hasOwnProperty(index)) {
          clonedMinimizedDialogs[index].active = false;
        }
      }

      if (dialogToRestoreIndex !== -1 && dialogToRestore) {
        dialogToRestore.active = true;
        dialogToRestore.restored = true;
        clonedMinimizedDialogs.splice(dialogToRestoreIndex, 1, dialogToRestore);
      }

      return {
        ...state,
        minimizedDialogs: clonedMinimizedDialogs,
      };

    /** Close Minimized Dialog */
    case MainActionTypes.CloseMinimizedDialog:
      clonedMinimizedDialogs = JSON.parse(JSON.stringify(state.minimizedDialogs));
      // eslint-disable-next-line no-case-declarations
      const dialogToCloseIndex = clonedMinimizedDialogs && clonedMinimizedDialogs.length ?
        clonedMinimizedDialogs.findIndex((item: MinimizedDialogInfoModel) => item.dialogId === action.payload) :
        -1;

      if (dialogToCloseIndex !== -1) {
        clonedMinimizedDialogs.splice(dialogToCloseIndex, 1);
      }

      /** Make Last Resumed Dialog active */
      if (clonedMinimizedDialogs && clonedMinimizedDialogs.length) {
        const restoredDialogs = clonedMinimizedDialogs.filter((item: MinimizedDialogInfoModel) => item.restored);
        if (restoredDialogs && restoredDialogs.length) {
          const lastRestoredDialog = restoredDialogs[restoredDialogs.length - 1];
          if (lastRestoredDialog) {
            lastRestoredDialog.active = true;
            const dialogIndexToReplaceIndex = clonedMinimizedDialogs.findIndex((item: MinimizedDialogInfoModel) => {
              return item.dialogId === lastRestoredDialog.id;
            });
            const dialogIndexToReplace = clonedMinimizedDialogs.find((item: MinimizedDialogInfoModel) => {
              return item.dialogId === lastRestoredDialog.id;
            });
            if (dialogIndexToReplaceIndex !== -1 && dialogIndexToReplace) {
              clonedMinimizedDialogs.splice(dialogIndexToReplaceIndex, 1, lastRestoredDialog);
            }
          }
        }
      }

      return {
        ...state,
        minimizedDialogs: clonedMinimizedDialogs,
      };

    /** Sip State */
    case MainActionTypes.ChangeSipState:
      // eslint-disable-next-line no-case-declarations
      const currentSipState = JSON.parse(JSON.stringify(state.sipState));
      return {
        ...state,
        sipState: {...currentSipState, ...action.payload}
      };

    /** Call State */
    case MainActionTypes.ChangeCallState:
      // eslint-disable-next-line no-case-declarations
      const currentCallState = JSON.parse(JSON.stringify(state.callState));
      return {
        ...state,
        callState: {...currentCallState, ...action.payload}
      };

    case MainActionTypes.ResetCallState:
      // eslint-disable-next-line no-case-declarations
      return {
        ...state,
        callState: {}
      };

    /** Get Calls List */
    case MainActionTypes.GetCallsList:
      return {...state, callsListLoading: true};

    case MainActionTypes.GetCallsListSuccess:
      return {
        ...state,
        callsList:
          state.callsList && action.payload.mergeItems ? [...state.callsList, ...action.payload.response.content!] : action.payload.response.content!,
        callsListPagination: {
          size: action.payload.response.size,
          last: action.payload.response.last,
          totalElements: action.payload.response.totalElements,
          numberOfElements: action.payload.response.numberOfElements,
          first: action.payload.response.first,
          number: action.payload.response.number,
        },
        callsListLoading: false,
      };

    case MainActionTypes.GetCallsListFailure:
      return {...state, callsListLoading: false};

    /** Change Page */
    case MainActionTypes.ChangeCallsListPage:
      return {
        ...state,
        callsListPagination: {
          ...state.callsListPagination,
          number: action.payload.number,
        }
      };

    /** Get Notifications List */
    case MainActionTypes.GetNotificationsList:
      return {...state, notificationsListLoading: true};

    case MainActionTypes.GetNotificationsListSuccess:
      return {
        ...state,
        notificationsList:
          state.notificationsList && action.payload.mergeItems ? [...state.notificationsList, ...action.payload.response.content!] : action.payload.response.content!,
        notificationsListPagination: {
          size: action.payload.response.size,
          last: action.payload.response.last,
          totalElements: action.payload.response.totalElements,
          numberOfElements: action.payload.response.numberOfElements,
          first: action.payload.response.first,
          number: action.payload.response.number,
        },
        notificationsListLoading: false,
      };

    case MainActionTypes.GetNotificationsListFailure:
      return {...state, notificationsListLoading: false};

    /** Change Page */
    case MainActionTypes.ChangeNotificationsListPage:
      return {
        ...state,
        notificationsListPagination: {
          ...state.notificationsListPagination,
          number: action.payload.number,
        }
      };

    /** Set Initial State */
    case MainActionTypes.SetInitialState:
      return {...initialState};

    default:
      return state;
  }
}
